import PlayStep from "../wizard/play-step";
import StateBuilder from "../sports/football/state/state-builder";
import { getSafe, getSafeOrDefault } from "./common/object-util";
import FootballState from "../sports/football/state/football-state";
import FootballTransition from "../sports/football/state/football-transition";
import FootballStringifier from "./stringifiers/football-stringifier";

const excludedStates = [
    FootballState.Start,
    FootballState.PlayPenaltyOffsetting,
    FootballState.PlayPenaltySecondOffsetting,
    FootballState.Offsetting
];

const customStateData = {
    [FootballState.AddPlay]: { displayName: "Play Entry" },
    [FootballState.GameControl]: {
        displayName: "Game Control",
        title: "Game Control",
        subtitle: " "
    },
    [FootballState.StartDrive]: {
        displayName: "Save Drive",
        title: "Save Drive",
        subtitle: " "
    },
    [FootballState.PlayPenaltyStatsCount]: {
        displayName: "Do stats count?",
        title: "Do stats count?",
        subtitle: "Indicate if stats will count for this play"
    },
    [FootballState.StatsCount]: {
        displayName: "Do stats count?",
        title: "Do stats count?",
        subtitle: "Indicate if stats will count for this play"
    }
};

class WizardService {
    getAssociatedFragment(play, stateName, historyIndex) {
        return play.fragments.find(
            (f) =>
                f.associatedState === stateName &&
                f.associatedHistoryIndex === historyIndex
        );
    }

    resolveText(object, property, param) {
        return object[property] && typeof object[property] === "function"
            ? object[property](param)
            : object[property];
    }

    allPreviousFragmentsCompleted(play, historyIndex) {
        return !play.fragments.find(
            (f) =>
                f.associatedHistoryIndex < historyIndex && !f.hasRequiredData()
        );
    }

    shouldIncludeResultStep(
        play,
        state,
        fragment,
        historyIndex,
        teams,
        coinTossRequired,
        gameContext,
        service
    ) {
        const stateTransitions = service._getAllowedTransitions(
            play,
            fragment,
            teams,
            coinTossRequired,
            gameContext
        );
        const fragmentTransitions = play.getStateTransitions(
            fragment.associatedState
        );

        const canOnlyFinalize =
            fragmentTransitions.length === 1 &&
            fragmentTransitions[0].name === FootballTransition.Finalize;
        const isSpecialFragment = [
            FootballState.EndOfPlay,
            FootballState.NoPenaltyEndOfPlay,
            FootballState.StatsCount,
            FootballState.AddPlay
        ].some((state) => state === fragment.associatedState);
        const partialData = !fragment || fragment.hasRequiredPartialData();
        const allPreviousFragmentsCompleted =
            this.allPreviousFragmentsCompleted(play, historyIndex);

        return (
            (stateTransitions.length > 1 ||
                isSpecialFragment ||
                canOnlyFinalize) &&
            partialData &&
            allPreviousFragmentsCompleted
        );
    }

    shouldIncludeAfterNextState(rd, nextFragment) {
        return (
            nextFragment &&
            rd.requiredAfterNextState &&
            rd.requiredAfterNextState.find(
                (s) => s === nextFragment.associatedState
            )
        );
    }

    shouldIncludeAfterResult(rd, nextFragment, play) {
        return (
            rd.requiredAfterNextState &&
            (nextFragment ||
                [
                    FootballState.EndOfPlay,
                    FootballState.NoPenaltyEndOfPlay
                ].includes(play.getState()))
        );
    }

    getStateSteps(
        play,
        state,
        historyIndex,
        homeTeam,
        awayTeam,
        gameContext,
        validationContext = {},
        coinTossRequired,
        service
    ) {
        let stateSteps = [];
        let resultSteps = [];
        const afterNextStateSteps = [];
        const fragment = this.getAssociatedFragment(play, state, historyIndex);

        if (fragment) {
            const teamId = play.resolveFragmentTeamId(play, fragment);

            if (
                this.shouldIncludeResultStep(
                    play,
                    state,
                    fragment,
                    historyIndex,
                    { homeTeam, awayTeam },
                    coinTossRequired,
                    gameContext,
                    service
                )
            ) {
                const { displayName, title, subtitle } =
                    customStateData[state] || {};
                const { displayName: defaultDisplayName } =
                    StateBuilder.getByName(state) || { displayName: state };

                resultSteps.push(
                    new PlayStep({
                        play,
                        displayName: getSafeOrDefault(
                            () => displayName,
                            `${defaultDisplayName} Result`
                        ),
                        fragment,
                        state: state,
                        title: getSafeOrDefault(() => title, "Result"),
                        subtitle: getSafeOrDefault(
                            () => subtitle,
                            `Select result of the ${defaultDisplayName}`
                        ),
                        isResult: true,
                        canBeFinalized: play.canBeFinalized(state),
                        validationMessages: validationContext.messages
                    })
                );
            }

            const { requiredData } = fragment;
            if (requiredData) {
                const { summaryContext } = FootballStringifier.generateSummary(
                    play,
                    gameContext
                );
                requiredData
                    .filter((rd) => {
                        return (
                            rd.type &&
                            !fragment.excludeRequiredData(
                                rd,
                                gameContext.gameConfig
                            )
                        );
                    })
                    .forEach((rd) => {
                        const stepTeamId = play.resolveStepTeamId(
                            rd,
                            teamId,
                            gameContext
                        );
                        const team =
                            stepTeamId === homeTeam.id ? homeTeam : awayTeam;
                        const defaultValue = this.resolveText(
                            rd,
                            "defaultValue",
                            {
                                play,
                                summaryContext,
                                team
                            }
                        );

                        const restrictedPositions =
                            this.resolveText(rd, "restrictedPositions", {
                                play,
                                gameConfig: gameContext.gameConfig
                            }) || [];

                        const step = new PlayStep({
                            play,
                            displayName: rd.displayName,
                            fragment,
                            requiredData: {
                                ...rd,
                                type: getSafeOrDefault(
                                    () => rd.type(play),
                                    rd.type
                                )
                            },
                            state: state,
                            title: this.resolveText(rd, "title", team),
                            subtitle: this.resolveText(rd, "subtitle", team),
                            positionPref: this.resolveText(
                                rd,
                                "positionPref",
                                play
                            ),
                            defaultValue,
                            restrictedPositions,
                            team,
                            canBeFinalized: play.canBeFinalized(state)
                        });

                        const nextFragment = play.getNextFragment(fragment);
                        if (
                            this.shouldIncludeAfterNextState(rd, nextFragment)
                        ) {
                            afterNextStateSteps.push(step);
                        } else if (
                            resultSteps.length &&
                            this.shouldIncludeAfterResult(
                                rd,
                                nextFragment,
                                play
                            )
                        ) {
                            resultSteps.push(step);
                        } else if (!rd.requiredAfterNextState) {
                            stateSteps.push(step);
                        }
                    });
            }
        }

        return { stateSteps, resultSteps, afterNextStateSteps };
    }

    getSteps(
        play,
        homeTeam,
        awayTeam,
        gameContext,
        validationContext,
        coinTossRequired,
        service
    ) {
        let steps = [];
        if (play && gameContext) {
            const { _state } = play;
            const { history } = _state;

            let afterNextStateSteps = [];
            history.forEach((state, historyIndex) => {
                if (!excludedStates.includes(state)) {
                    const {
                        stateSteps,
                        resultSteps,
                        afterNextStateSteps: currentAfterNextStateSteps
                    } = this.getStateSteps(
                        play,
                        state,
                        historyIndex,
                        homeTeam,
                        awayTeam,
                        gameContext,
                        validationContext,
                        coinTossRequired,
                        service
                    );

                    if (
                        [
                            FootballState.EndOfPlay,
                            FootballState.NoPenaltyEndOfPlay
                        ].includes(state)
                    ) {
                        steps = [
                            ...steps,
                            ...afterNextStateSteps,
                            ...stateSteps,
                            ...resultSteps
                        ];
                    } else {
                        steps = [
                            ...steps,
                            ...stateSteps,
                            ...afterNextStateSteps,
                            ...resultSteps
                        ];
                    }

                    afterNextStateSteps = currentAfterNextStateSteps;
                }
            });

            const pendingFragment = play.getPendingFragment();
            if (pendingFragment) {
                const { stateSteps, resultSteps, afterNextStateSteps } =
                    this.getStateSteps(
                        play,
                        pendingFragment.associatedState,
                        history.length,
                        homeTeam,
                        awayTeam,
                        gameContext,
                        validationContext,
                        coinTossRequired,
                        service
                    );
                steps = [
                    ...steps,
                    ...stateSteps,
                    ...afterNextStateSteps,
                    ...resultSteps
                ];
            }

            steps.forEach((s) => {
                const { isResult } = s;
                const historyIndex = getSafe(
                    () => s.fragment.associatedHistoryIndex
                );
                if (isResult && historyIndex) {
                    s.nextTransition = play.getTransitionAfter(historyIndex);
                }
            });
        }

        return steps.map((s, index) => {
            return Object.assign(s, { index: index });
        });
    }

    getActiveStepIndex(play, steps) {
        let stepIndex = 0;

        if (play) {
            if (steps.length > 0) {
                stepIndex = steps.length - 1;
            }

            const firstNotCompleteIndex = steps.findIndex(
                (s) => !s.isComplete()
            );

            if (firstNotCompleteIndex >= 0) {
                stepIndex = firstNotCompleteIndex;
            }
        }

        return stepIndex;
    }
}

export default new WizardService();
